export type CollectionPhase = 'PREVENTIVE' | 'OVERDUE';
export type PaymentInstruction = 'BOLETO' | 'TRANSFER';
export type ReceivingAccountKind = 'AGROLEND' | 'ESCROW';
export type ObligorRole = 'PAYER' | 'ASSIGNOR' | 'GUARANTOR';
export type NonBusinessDayRule = 'ANTICIPATE' | 'SKIP' | 'POSTPONE';
export type ActivationStatus = 'ACTIVE' | 'INACTIVE';
export type CollectionChannel = 'EMAIL' | 'EXTERNAL_MESSAGE';

export const PHASE_LABEL: Record<CollectionPhase, string> = { PREVENTIVE: 'Antes do vencimento', OVERDUE: 'Depois do vencimento' };
export const PAYMENT_LABEL: Record<PaymentInstruction, string> = { BOLETO: 'Boleto', TRANSFER: 'TED ou PIX' };
export const ACCOUNT_KIND_LABEL: Record<ReceivingAccountKind, string> = { AGROLEND: 'Agrolend', ESCROW: 'Escrow' };
export const OBLIGOR_LABEL: Record<ObligorRole, string> = { PAYER: 'Pagador da fatura', ASSIGNOR: 'Cedente', GUARANTOR: 'Avalista' };
export const NON_BUSINESS_DAY_LABEL: Record<NonBusinessDayRule, string> = {
    ANTICIPATE: 'Dia útil anterior', SKIP: 'Não cobrar', POSTPONE: 'Dia útil seguinte',
};
export const CHANNEL_LABEL: Record<CollectionChannel, string> = { EMAIL: 'E-mail', EXTERNAL_MESSAGE: 'WhatsApp' };
export const ACTIVATION_LABEL: Record<ActivationStatus, string> = { ACTIVE: 'Ativa', INACTIVE: 'Desativada' };

export const optionsOf = <T extends string>(labels: Record<T, string>) =>
    (Object.keys(labels) as T[]).map(value => ({ value, label: labels[value] }));

export interface PreventiveSchedule {
    daysBefore: number[];
    nonBusinessDayRule: NonBusinessDayRule;
}

export interface OverdueSchedule {
    firstAfterDays: number | null;
    everyDays: number | null;
    nonBusinessDayRule: NonBusinessDayRule;
}

export interface PolicySchedules {
    preventive: PreventiveSchedule | null;
    overdue: OverdueSchedule | null;
}

export const DEFAULT_PREVENTIVE_SCHEDULE: PreventiveSchedule = { daysBefore: [5], nonBusinessDayRule: 'ANTICIPATE' };
export const DEFAULT_OVERDUE_SCHEDULE: OverdueSchedule = { firstAfterDays: 1, everyDays: 7, nonBusinessDayRule: 'ANTICIPATE' };

export const SCHEDULE_KEY: Record<CollectionPhase, keyof PolicySchedules> = { PREVENTIVE: 'preventive', OVERDUE: 'overdue' };

export interface RuleCriteria {
    fromDaysPastDue: number | null;
    assignorCount: 1 | 2 | null;
    receivingAccountKind: ReceivingAccountKind | null;
    paymentInstruction: PaymentInstruction | null;
}

export interface MessageRef {
    messageCompositionId: string | null;
}

export interface CollectionRule extends MessageRef {
    id: string;
    phase: CollectionPhase;
    criteria: RuleCriteria;
    channel: CollectionChannel;
    obligorRole: ObligorRole;
    skip: boolean;
}

export interface CollectionPolicy {
    id: string;
    serialId?: number;
    name: string;
    description: string;
    status: ActivationStatus;
    statusReason: string | null;
    schedules: PolicySchedules;
    rules: CollectionRule[];
}

const joinDays = (days: number[]) => {
    const sorted = [...days].sort((x, y) => y - x).map(String);
    return sorted.length > 1 ? `${sorted.slice(0, -1).join(', ')} e ${sorted[sorted.length - 1]}` : sorted.join('');
};

export const preventiveLabel = (schedule: PreventiveSchedule | null): string =>
    (schedule && schedule.daysBefore.length ? `${joinDays(schedule.daysBefore)} dias antes` : '—');

export const overdueLabel = (schedule: OverdueSchedule | null): string =>
    (schedule && schedule.firstAfterDays != null && schedule.everyDays != null
        ? `1ª ${schedule.firstAfterDays} ${schedule.firstAfterDays === 1 ? 'dia' : 'dias'} depois, a cada ${schedule.everyDays} ${schedule.everyDays === 1 ? 'dia' : 'dias'}`
        : '—');

export const newId = (prefix: string) => `${prefix}-${Date.now()}-${Math.round(Math.random() * 1e6)}`;
